/* eslint-disable */
import React, { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faExclamation, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { faCircle } from '@fortawesome/free-regular-svg-icons';
import { Col, Row, Form, Nav } from 'react-bootstrap';
import { unwrapResult } from '@reduxjs/toolkit';
import { s } from '../../app/helper';
import { PrimaryButton, SecondaryOutlineButton, SmallSecondaryOutlineButton } from '../button/Button';
import {
  GroupedOption,
  SelectOption,
  UploadFormCheckArea,
  UploadFormSelect,
  UploadFormTextInput,
} from '../uploadFormInput/UploadFormInput';

import branch_options from '../../app/branch_options.json';

import styles from './UploadDocument.module.css';
import fontStyles from '../text/Text.module.css';
import { createReport, getReports } from '../../reducer/reportsSlice';
import { useAppDispatch } from '../../app/hooks';
import UploadFormFileInput from '../uploadFormInput/UploadFormFileInput';

type UploadFormProps = {
  onHide: () => void;
};

const branchOptions = branch_options as GroupedOption[];
const optionsToSelectOptions = (options: string[]): SelectOption[] => options.map((op) => ({
  label: op,
  value: op
    .toLowerCase()
    .replaceAll(/\s+/g, '_')
    .replaceAll('&', 'and'),
}));

const fileTo64 = async (file: File): Promise<string> => new Promise<string>(
  (resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onerror = (error) => reject(error);
    // need to remove a prefix added by readAsDataURL so we get a base64 st
    fileReader.onload = () => {
      const dataUrl = fileReader.result as string;
      const b64str = dataUrl.split(',').pop() as string;
      resolve(b64str);
    };
  },
);

// function useData<T>(initialData:T) : [T, (key: keyof T) => (value: string | number) => void] {
//   const [data, setData] = useState<T>(initialData);
//   const handleDataChange = (key: keyof T) => (value: string | number): void => setData((uD) => ({
//     ...uD,
//     [key]: typeof uD[key] === 'number' ? Number(value) : value,
//   }));

//   return [data, handleDataChange];
// }

export default function UploadForm({ onHide }: UploadFormProps):JSX.Element {
  const [t] = useTranslation('upload_document');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [formIsValidated, setFormIsValidated] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectorIsSelected, setSelectorIsSelected] = useState({ company: false, year: false });
  const [errorMessage, setErrorMessage] = useState<string>('');
  const yearSelectOptions = optionsToSelectOptions(['2019', '2020', '2021', '2022', '2023', '2024']);

  const handleUpload = (e:FormEvent<HTMLFormElement>):void => {
    e.preventDefault();
    e.stopPropagation();
    setFormIsValidated(true);
    const form = e.currentTarget;
    // const formData = new FormData(form);
    // console.log(Array.from(formData.entries()));
    if (form.checkValidity()) {
      setIsSubmitted(true);
      dispatch(createReport({ formData: new FormData(form) }))
        .then(unwrapResult)
        .then(({ job_id }) => {
          // TODO: backend change needed, instead of fetching all reports, just add the new one
          // this dispatch is important because otherwise the new report will not be shown, the redux state is empty
          dispatch(getReports());
          onHide();
          // navigate(`/report/${job_id}/`);
        }).catch((err) => {
          console.error({ err });
          setErrorMessage('An error occurred while uploading the document');
        });
    }
  };

  return (
    <>
      <div className={styles.uploadModalHeader}>
        <span className={s(fontStyles.HeaderPopups, styles.uploadModalTitle)}>
          {t('upload-new-doc')}
        </span>
        <SecondaryOutlineButton
          className={styles.closeUploadModal}
          onClick={onHide}
        >
          <FontAwesomeIcon
            icon={faTimes}
            className={styles.crossIcon}
            size="2x"
          />
        </SecondaryOutlineButton>
      </div>
      <div>{t('upload-doc')}</div>
      <Form
        noValidate
        validated={formIsValidated}
        onSubmit={handleUpload}
      >
        <Row className="w-50 py-3 gy-3" xs={1}>
          <Col>
            <UploadFormTextInput
              keyName="report_name"
              required
              className="m-0"
              label={t('report-name')}
            />
          </Col>
          <Col>
            <UploadFormSelect
              keyName="company"
              required
              className="m-0"
              options={branchOptions}
              label={t('company')}
              warning={formIsValidated && !selectorIsSelected.company ? '!!' : undefined}
              onChange={() => setSelectorIsSelected((prev) => ({ ...prev, company: true }))}
            />
          </Col>
          <Col xs={6}>
            <UploadFormSelect
              keyName="year"
              required
              className="m-0"
              options={yearSelectOptions}
              label={t('year')}
              warning={formIsValidated && !selectorIsSelected.year ? '!!' : undefined}
              onChange={() => setSelectorIsSelected((prev) => ({ ...prev, year: true }))}
            />
          </Col>
          <Col>
            <UploadFormFileInput
              keyName="de_file"

              required
              label="DOKUMENT IN DEUTSCH HOCHLADEN"
                formIsValidated={formIsValidated}
            />
          </Col>
          <Col>
            <UploadFormFileInput
              keyName="en_file"

              required
              label="DOKUMENT IN ENGLISCH HOCHLADEN"
                formIsValidated={formIsValidated}
            />
          </Col>
        </Row>

        <UploadFormCheckArea
          prompt={t('tos')}
          keyName="check"
          required
          // value={tosValue}
          labelClassName={s(styles.tosLabel, fontStyles.text)}
          checkClassName={styles.tosCheck}
          className={styles.tosBox}
          // onChange={setTosValue}
        />
        <SmallSecondaryOutlineButton>
          <Nav.Link href='/datenschutz'>{t('data-protection')}</Nav.Link>
        </SmallSecondaryOutlineButton>
        <Row className={styles.uploadModalRow}>
          <Col xs={12} className={styles.uploadModalButtonBar}>
            <PrimaryButton
              type="submit"
              // disabled={!tosValue}
                            // className={styles.uploadModalButtonBarButton}
              className="rounded-4"
            >
              {t('continue')}
            </PrimaryButton>
            {isSubmitted && <FontAwesomeIcon icon={faSpinner} size="lg" spin />}
            <PrimaryButton
              className="rounded-4"
                            // className={styles.uploadModalButtonBarButton}
              onClick={onHide}
            >
              {t('back-overview')}
            </PrimaryButton>
          </Col>
        </Row>
        <Row className={styles.uploadModalRow}>
          {errorMessage ? (
            <div className={s(styles.errorBox)}>
              <div className={styles.errorBoxHeader}>
                <span className={s('fa-stack fa-1x', styles.exclamationIcon)}>
                  <FontAwesomeIcon className={s('fa-stack-2x')} icon={faCircle} />
                  <FontAwesomeIcon
                    className={s('fa-stack-1x')}
                    icon={faExclamation}
                  />
                </span>
                <span className={s(fontStyles.SubHeaderPages)}>
                  {t('an-error-occurred')}
                </span>
              </div>
              {errorMessage}
            </div>
          ) : null}
        </Row>
      </Form>
    </>
  );
}

// <input
//   className="d-none"
//   ref={inputFileRef1}
//   type="file"
//   accept=".pdf"
//   name="de_file"
//   required
//   // event.target?.files?.[0] === inputFileRef1.current?.files?.[0]
//   // onChange={(/* event:any */) => setUploadFile(inputFileRef1.current?.files?.[0])}
// />
// <PrimaryButton
//   className="rounded-circle p-2 me-3"
//   onClick={() => inputFileRef1.current?.click()}
// >
//   <FontAwesomeIcon icon={faCloudArrowUp} />
// </PrimaryButton>
// DOKUMENT IN DEUTSCH HOCHLADEN

// <input
//   className="d-none"
//   ref={inputFileRef2}
//   type="file"
//   accept=".pdf"
//   name="en_file"
//   required
//   // event.target?.files?.[0] === inputFile2.current?.files?.[0]
//   // onChange={(/* event:any */) => setUploadFile(inputFileRef2.current?.files?.[0])}
// />
// <PrimaryButton
//   className="rounded-pill me-3"
//   onClick={() => inputFileRef2.current?.click()}
// >
//   <FontAwesomeIcon icon={faCloudArrowUp} />
// </PrimaryButton>
// DOKUMENT IN ENGLISCH HOCHLADEN
